import { FC } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import loaderAnimation from "../../assets/loader.png";

interface SpinnerProps {
  message?: string;
  size?: "small" | "medium" | "large";
  variant?: "overlay" | "centered" | "inline";
}

const Spinner: FC<SpinnerProps> = ({ message, size = "medium", variant = "centered" }) => {
  const { t } = useTranslation();
  const displayMessage = message || t("loading");
  const sizeMap = {
    small: 80,
    medium: 120,
    large: 180,
  };

  const videoSize = sizeMap[size];

  const content = (
    <>
      {/* Animated PNG with a transparent background — renders as a plain <img>,
          so it works identically across Chrome/Safari/Firefox with no codec
          or blend-mode issues (unlike the old WebM, which had a baked-in black
          background and didn't decode in Safari). */}
      <img
        src={loaderAnimation}
        alt=""
        aria-hidden
        style={{
          width: videoSize,
          height: videoSize,
          objectFit: "contain",
        }}
      />
      {displayMessage && (
        <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 400, mt: 1 }}>
          {displayMessage}
        </Typography>
      )}
    </>
  );

  if (variant === "overlay") {
    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999,
          gap: 2,
        }}
      >
        {content}
      </Box>
    );
  }

  if (variant === "centered") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 200px)", // Full viewport height minus approximate header/nav height
          gap: 2,
        }}
      >
        {content}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: size === "small" ? "100px" : "200px",
        gap: 2,
        py: 3,
      }}
    >
      {content}
    </Box>
  );
};

export default Spinner;
